import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { NO_WIFI_EVENT } from "@/lib/network";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * Global "No Wifi" popup. Raised whenever a feature that needs the internet is
 * used while the device is offline.
 */
export const NoWifiDialog = () => {
  const [open, setOpen] = useState(false);
  const online = useOnlineStatus();
  const { t } = useLanguage();

  useEffect(() => {
    let last = 0;
    const onNoWifi = () => {
      // Debounce bursts of failed requests into one popup.
      const now = Date.now();
      if (now - last < 1200) return;
      last = now;
      setOpen(true);
    };
    window.addEventListener(NO_WIFI_EVENT, onNoWifi);
    return () => window.removeEventListener(NO_WIFI_EVENT, onNoWifi);
  }, []);

  // Auto-dismiss once the connection comes back.
  useEffect(() => {
    if (online) setOpen(false);
  }, [online]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      onClick={() => setOpen(false)}
    >
      <div
        className="glass-panel w-full max-w-xs rounded-2xl p-6 text-center text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/15">
          <WifiOff className="h-6 w-6 text-destructive" />
        </div>
        <h2 className="text-xl font-bold tracking-tight">{t("No Wifi")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("This feature needs an internet connection. Games, your calendar and saved plans still work offline.")}
        </p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="glass-button mt-5 h-11 w-full rounded-xl font-semibold text-primary-foreground"
        >
          {t("Got it")}
        </button>
      </div>
    </div>
  );
};
